import { createContext, useContext, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";

// Context cho hệ thống hướng dẫn (tour).
// - useTour: tour từng trang (sau khi đã xong luồng, hoặc trang ngoài luồng)
// - useLuongTour: tour luồng lần đầu theo vai trò (ưu tiên hơn tour trang)
// TourRunner là nơi DUY NHẤT render <Joyride>.
const TourContext = createContext();
TourContext.displayName = "TourContext";

function TourProvider({ children }) {
  // active: { tourId, steps, run, runSession, uid, laLuong, laChangCuoi }
  const [active, setActive] = useState(null);

  const registerTour = useCallback((tourId, steps, uid = null, meta = {}) => {
    const laLuong = !!meta.laLuong;
    setActive((cur) => {
      // Tour trang không được đè tour luồng đang giữ cùng phiên.
      if (cur?.laLuong && !laLuong && cur.tourId !== tourId) {
        return cur;
      }
      return {
        tourId,
        steps,
        uid,
        laLuong,
        laChangCuoi: !!meta.laChangCuoi,
        run: cur?.tourId === tourId ? cur.run : false,
        runSession: cur?.tourId === tourId ? cur.runSession ?? 0 : 0,
      };
    });
  }, []);

  const unregisterTour = useCallback((tourId) => {
    setActive((cur) => (cur?.tourId === tourId ? null : cur));
  }, []);

  const startActiveTour = useCallback(() => {
    setActive((cur) =>
      cur
        ? {
            ...cur,
            run: true,
            runSession: (cur.runSession ?? 0) + 1,
          }
        : cur
    );
  }, []);

  const stopActiveTour = useCallback(() => {
    setActive((cur) => (cur ? { ...cur, run: false } : cur));
  }, []);

  const value = useMemo(
    () => ({ active, registerTour, unregisterTour, startActiveTour, stopActiveTour }),
    [active, registerTour, unregisterTour, startActiveTour, stopActiveTour]
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

TourProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

function useTourContext() {
  const context = useContext(TourContext);

  if (!context) {
    throw new Error("useTourContext phải dùng bên trong TourProvider.");
  }

  return context;
}

export { TourProvider, useTourContext };
